import {
  Box,
  Circle,
  type EnhancedDimension,
  Table,
  Tag,
  Text,
  colors,
} from '@factorialco/deprecated-design-system'
import { WorkScheduleOverlapPeriodFragment } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateRange } from 'modules/core/lib/dates'
import { weekdaysWithUserFirstDayOfWeek } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import DayConfigRowBreaks from './DayConfigRowBreaks'
import EmptyScheduleDay from './EmptyScheduleDay'
import type { DayConfigurationWithBreaks } from './types'

interface Props {
  period: WorkScheduleOverlapPeriodFragment
  dayConfigsPerPeriod: DayConfigurationWithBreaks[]
}

interface Config {
  startAt: string
  endAt: string
}

const DayConfigSchedule = ({ startAt, endAt }: Config) => {
  const isOvernightConfig = startAt > endAt

  return (
    <Box width="full" flexDirection="row" gap="s4" alignItems="center">
      <Tag type="grey">
        {startAt} - {endAt}
      </Tag>
      {isOvernightConfig && (
        <Text.Normal size="100">{i18n.t('shift_management.plus_1_day_tag')}</Text.Normal>
      )}
    </Box>
  )
}

const TableRowFixedSchedule = ({ dayConfigsPerPeriod, period }: Props) => {
  const startDate = period.startDate
  const endDate = period.endDate
  const isDefault = period.default
  const { access } = useCurrentSession()
  const timeFormat = access.timeFormat
  const firstDayOfWeek = access.firstDayOfWeek

  const isTimeFormat24H = timeFormat === 24
  const width: EnhancedDimension = isTimeFormat24H ? 's140' : 's200'
  const periodRange = dateRange(startDate, endDate)

  const dayConfigsByDay =
    dayConfigsPerPeriod &&
    groupBy(dayConfigsPerPeriod, ({ dayConfiguration }) => dayConfiguration.weekday)

  const schedulePerDay = (day: string) => {
    const configsByDay = dayConfigsByDay[day]

    if (!configsByDay) {
      return (
        <Box width={width} justifyContent="center">
          <EmptyScheduleDay />
        </Box>
      )
    }

    return configsByDay.map(({ dayConfiguration, breaks }, index) => {
      const startAt = dayConfiguration.formattedStartAt || ''
      const endAt = dayConfiguration.formattedEndAt || ''

      return (
        <Box width={width} key={`${day}-${index}`} justifyContent="center" gap="s8">
          <DayConfigSchedule startAt={startAt} endAt={endAt} />
          <DayConfigRowBreaks plannedBreaks={breaks} />
        </Box>
      )
    })
  }

  return (
    <Table.Row>
      <Table.Cell verticalAlign="top">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Circle color={isDefault ? colors.viridian1000 : colors.champagne1000} size={8} />
          <Text.Normal color="grey1000">{periodRange}</Text.Normal>
        </Box>
      </Table.Cell>
      {weekdaysWithUserFirstDayOfWeek(firstDayOfWeek).map((day, i) => {
        return (
          <Table.Cell wide key={`${day}-${i}`} verticalAlign="top" paddingX="s8">
            <Box gap="s8">
              <Box width="full" gap="s16">
                {schedulePerDay(day)}
              </Box>
            </Box>
          </Table.Cell>
        )
      })}
    </Table.Row>
  )
}

export default observer(TableRowFixedSchedule)
